/**
 * Bought/sold, single/multi-leg and opening/closing for a scanner row.
 *
 * Everything here is derived from fields the tape already carries — the aggressor side, the
 * OPRA sale condition, and volume against open interest — so a board can show what the flow
 * actually was without a vendor request per row. The heavier per-contract verification
 * (`/api/ois/contract-verdict`) exists for one contract at a time; this is the cheap version
 * that can run on every row of a 150-row board.
 *
 * Where a fact is not available it says so. `flow_buffer` never records open interest, so the
 * aggregate board legitimately cannot answer opening-vs-closing and returns `null` rather
 * than a guess dressed up as a reading.
 */

/**
 * OPRA sale conditions that state the execution structure.
 *
 * Kept in step with `app/contract_verdict.py` and the SQL lists in `flow_query_engine.py`.
 * Note that SINGLE_LEG_* also contains "LEG", so a substring rule would classify lone orders
 * as spreads — the codes are enumerated for that reason.
 */
export const MULTI_LEG_CONDITION_CODES = new Set([
  35, 38, 67, 124, 130, 131, 132, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144,
]);

export const SINGLE_LEG_CONDITION_CODES = new Set([125, 126, 127, 128, 129]);

export type FlowDirectionLabel = 'Bought' | 'Sold' | 'Two-sided' | null;
export type FlowStructureLabel = 'Multi-leg' | 'Single-leg' | 'Mixed' | null;
export type FlowPositionLabel = 'Opening' | 'Closing' | 'Flat' | 'Likely opening' | 'Indeterminate' | null;

/** Share of session volume that must carry a structural condition before it becomes a verdict. */
const MIN_STRUCTURE_COVERAGE = 0.4;

/**
 * Direction from the premium split, not the contract split.
 *
 * Premium because that is the money at risk: one large block against the offer plus many
 * small sells is a buy, and weighting by contracts would call it a sell.
 */
export function directionFromPremiumSplit(
  buyPremium: number | null | undefined,
  sellPremium: number | null | undefined,
): { label: FlowDirectionLabel; buyPct: number | null } {
  const buy = Number(buyPremium ?? 0);
  const sell = Number(sellPremium ?? 0);
  const decided = buy + sell;
  if (!Number.isFinite(decided) || decided <= 0) return { label: null, buyPct: null };

  const buyPct = (100 * buy) / decided;
  if (buyPct >= 55) return { label: 'Bought', buyPct };
  if (buyPct <= 45) return { label: 'Sold', buyPct };
  return { label: 'Two-sided', buyPct };
}

/** Direction for a single print, from the aggressor side the classifier resolved. */
export function directionFromTapeSide(side: string | null | undefined): FlowDirectionLabel {
  const s = String(side ?? '').trim().toLowerCase();
  if (s === 'ask' || s === 'buy') return 'Bought';
  if (s === 'bid' || s === 'sell') return 'Sold';
  return null;
}

/**
 * Structure from the labelled share of volume.
 *
 * Volume-weighted, and only when enough of the volume carries a structural condition at all:
 * plenty of ordinary fills print with just an auto-execution code, so on a thin contract the
 * labelled slice can be a small and unrepresentative minority.
 */
export function structureFromLegSplit(
  multiLegContracts: number | null | undefined,
  singleLegContracts: number | null | undefined,
  totalContracts?: number | null,
): { label: FlowStructureLabel; multiPct: number | null } {
  const multi = Number(multiLegContracts ?? 0);
  const single = Number(singleLegContracts ?? 0);
  const labelled = multi + single;
  if (!Number.isFinite(labelled) || labelled <= 0) return { label: null, multiPct: null };

  const total = Number(totalContracts ?? 0) > 0 ? Number(totalContracts) : labelled;
  if (labelled / total < MIN_STRUCTURE_COVERAGE) return { label: null, multiPct: null };

  const multiPct = (100 * multi) / labelled;
  if (multiPct >= 60) return { label: 'Multi-leg', multiPct };
  if (multiPct <= 40) return { label: 'Single-leg', multiPct };
  return { label: 'Mixed', multiPct };
}

/**
 * Structure from a rendered condition label such as `Multi-leg auction (#131)`.
 *
 * The imbalance report ships the condition as display text rather than a number, with the
 * code in parentheses. Parsing the code rather than matching the words keeps this working
 * when the vendor rewords a label, and avoids the trap that "Single-leg auction" and
 * "Multi-leg auction" differ by one token.
 */
export function structureFromConditionDisplay(text: unknown): FlowStructureLabel {
  const m = String(text ?? '').match(/#(\d+)/);
  return m ? structureFromCondition(Number(m[1])) : null;
}

/** Structure for a single print, straight off its sale condition. */
export function structureFromCondition(condition: unknown): FlowStructureLabel {
  const code = Number(condition);
  if (!Number.isFinite(code)) return null;
  if (MULTI_LEG_CONDITION_CODES.has(code)) return 'Multi-leg';
  if (SINGLE_LEG_CONDITION_CODES.has(code)) return 'Single-leg';
  return null;
}

/**
 * Opening or closing, as far as volume against open interest can say.
 *
 * Volume above open interest is arithmetic, not inference: only `openInterest` contracts
 * exist to be closed, so any excess had to be created. Below that line a single session
 * cannot separate the two, and this returns `Indeterminate` rather than picking one — a
 * confident "closing" that is really a coin flip is worse than no label.
 */
export function positionEffectFromVolOi(
  volume: number | null | undefined,
  openInterest: number | null | undefined,
): { label: FlowPositionLabel; volOi: number | null } {
  const vol = Number(volume ?? 0);
  const oi = Number(openInterest ?? 0);
  if (!Number.isFinite(vol) || !Number.isFinite(oi) || vol <= 0 || oi <= 0) {
    return { label: null, volOi: null };
  }
  const volOi = vol / oi;
  if (vol > oi) return { label: 'Opening', volOi };
  if (volOi >= 0.5) return { label: 'Likely opening', volOi };
  return { label: 'Indeterminate', volOi };
}

/**
 * Opening or closing from the change in open interest — the reading that is not a guess.
 *
 * Open interest rising means contracts were created; falling means they were retired. A
 * ratio of volume to open interest can only ever suggest this: 400 contracts traded against
 * an open interest of 400 is equally consistent with 400 opened, 400 closed, or 200 opened
 * and shut twice. The change distinguishes them outright.
 *
 * `Flat` is a real answer, not a missing one: volume with no net change in open interest is
 * positions opened and closed inside the session, which is worth knowing.
 *
 * The caveat that has to travel with this: open interest is stamped once, around 06:30 ET,
 * for the session that just ended. A change therefore describes *that* session's flow and
 * never the one currently trading — today's will not be known until tomorrow morning.
 */
export function positionEffectFromOiChange(
  change: number | null | undefined,
): { label: FlowPositionLabel; change: number | null } {
  if (change == null || !Number.isFinite(Number(change))) return { label: null, change: null };
  const n = Number(change);
  if (n > 0) return { label: 'Opening', change: n };
  if (n < 0) return { label: 'Closing', change: n };
  return { label: 'Flat', change: 0 };
}

/** Everything a row can say about itself, for the board's columns. */
export interface FlowVerdictLabels {
  direction: FlowDirectionLabel;
  buyPct: number | null;
  structure: FlowStructureLabel;
  multiPct: number | null;
  position: FlowPositionLabel;
  volOi: number | null;
  /** Net contracts created (+) or retired (−) over the session `oiDate` names. */
  oiChange: number | null;
  /** The session the change covers. Shown so the label cannot be read as live. */
  oiDate: string | null;
}

export const EMPTY_FLOW_VERDICT: FlowVerdictLabels = {
  direction: null,
  buyPct: null,
  structure: null,
  multiPct: null,
  position: null,
  volOi: null,
  oiChange: null,
  oiDate: null,
};

/** Labels for an aggregated contract bucket (many prints rolled up in the database). */
export function verdictFromAggregate(b: {
  buy_premium?: number | null;
  sell_premium?: number | null;
  multi_leg_contracts?: number | null;
  single_leg_contracts?: number | null;
  contracts?: number | null;
  volume?: number | null;
  oi?: number | null;
  oi_change?: number | null;
  oi_date?: string | null;
}): FlowVerdictLabels {
  const dir = directionFromPremiumSplit(b.buy_premium, b.sell_premium);
  const st = structureFromLegSplit(b.multi_leg_contracts, b.single_leg_contracts, b.contracts);
  // The change is preferred wherever it exists; the ratio is the fallback for a contract
  // whose prior session is unavailable, and it is a weaker claim by construction.
  const byChange = positionEffectFromOiChange(b.oi_change);
  const byRatio = positionEffectFromVolOi(b.volume ?? b.contracts, b.oi);
  return {
    direction: dir.label,
    buyPct: dir.buyPct,
    structure: st.label,
    multiPct: st.multiPct,
    position: byChange.label ?? byRatio.label,
    volOi: byRatio.volOi,
    oiChange: byChange.change,
    oiDate: b.oi_date ?? null,
  };
}

/** Labels for one enriched-tape print. */
export function verdictFromTapeRow(r: {
  side?: string | null;
  trade_condition?: unknown;
  condition?: unknown;
  volume?: number | null;
  size?: number | null;
  oi?: number | null;
}): FlowVerdictLabels {
  const direction = directionFromTapeSide(r.side);
  const structure =
    structureFromCondition(r.trade_condition) ?? structureFromCondition(r.condition);
  const pos = positionEffectFromVolOi(r.volume ?? r.size, r.oi);
  return {
    direction,
    buyPct: direction === 'Bought' ? 100 : direction === 'Sold' ? 0 : null,
    structure,
    multiPct: structure === 'Multi-leg' ? 100 : structure === 'Single-leg' ? 0 : null,
    position: pos.label,
    volOi: pos.volOi,
    oiChange: null,
    oiDate: null,
  };
}

/** One-line summary for a tooltip or a compact tile. */
export function verdictSummaryText(v: FlowVerdictLabels): string {
  const parts = [
    v.direction ?? 'direction unknown',
    v.structure ? v.structure.toLowerCase() : 'structure unknown',
    v.position ? v.position.toLowerCase() : 'open/close unknown',
  ];
  return parts.join(' · ');
}

// ── server-verified verdict ───────────────────────────────────────────────────
// The report states an aggressor and renders a sale condition, which is enough for a weak
// direction and structure reading. `flow_verdict_detail` is stronger: the server derives it
// from every print in that contract's session, and it is the ONLY source for
// opening-vs-closing, which needs session volume against open interest and so cannot be had
// from the report's fields at all. Where it is present it wins, field by field.

const DIRECTION_LABELS: readonly string[] = ['Bought', 'Sold', 'Two-sided'];
const STRUCTURE_LABELS: readonly string[] = ['Multi-leg', 'Single-leg', 'Mixed'];
const POSITION_LABELS: readonly string[] = [
  'Opening', 'Closing', 'Flat', 'Likely opening', 'Indeterminate',
];

function verdictNum(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function verdictObj(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/**
 * Labels from a server verdict, or null when it carries none.
 *
 * Only the vocabulary the board renders is accepted. The server also emits "Undetermined",
 * "Unclear", "Unlabelled" and "Unknown" for a contract whose tape did not settle the
 * question; those map to null so the tile says "Not Available" rather than showing a
 * non-answer as though it were one.
 */
export function verdictFromDetail(raw: unknown): FlowVerdictLabels | null {
  if (!raw || typeof raw !== 'object') return null;
  const detail = raw as Record<string, unknown>;
  const direction = verdictObj(detail.direction);
  const structure = verdictObj(detail.structure);
  const position = verdictObj(detail.position_effect);

  const pick = (value: unknown, allowed: readonly string[]): string | null => {
    const s = typeof value === 'string' ? value.trim() : '';
    return allowed.includes(s) ? s : null;
  };

  const labels: FlowVerdictLabels = {
    direction: pick(direction.label, DIRECTION_LABELS) as FlowDirectionLabel,
    buyPct: verdictNum(direction.buy_premium_pct),
    structure: pick(structure.label, STRUCTURE_LABELS) as FlowStructureLabel,
    multiPct: verdictNum(structure.multi_leg_volume_pct),
    position: pick(position.label, POSITION_LABELS) as FlowPositionLabel,
    volOi: verdictNum(position.vol_oi),
    oiChange: null,
    oiDate: null,
  };
  const carriesSomething =
    labels.direction !== null || labels.structure !== null || labels.position !== null
    || labels.buyPct !== null || labels.multiPct !== null || labels.volOi !== null;
  return carriesSomething ? labels : null;
}
